import React, { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Download, Home, MapPinOff, RefreshCw, Users, Wand2, X } from "lucide-react";
import { PageHeader, KpiCard } from "../../components/common";
import { TableRowsSkeleton } from "../../components/PaginationBar";
import { CoverageAlertsCard } from "../../components/CoverageAlertsCard";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "../../components/ui/toggle-group";
import { ZoneSelect } from "../../components/ZoneSelect";
import { ReportNotes, SourceBadge, UnassignedWardsEmpty, UnassignedWardsTable, useUnassignedWards } from "../../components/UnassignedWardsPanel";
import { errMsg, fmtDateTime } from "../../lib/api";

/**
 * Phase 92 — Municipal Admin page: one place to see every ward with a territory gap
 * (properties left without a covering collector), from the last auto-assign run or live.
 */
export default function UnassignedWards() {
  const { code } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Phase 137: zone filter (server-side); kept in the URL so the view is shareable / survives refresh
  const uw = useUnassignedWards(code, { initialZoneId: searchParams.get("zone_id") || "" });
  const [alertsKey, setAlertsKey] = useState(0); // Phase 156: reload the coverage alerts after a backfill
  const report = uw.report;
  const rows = report?.rows || [];
  const s = report?.summary || {};
  const job = report?.job;
  const onZone = (z) => {
    uw.setZone(z);
    const next = new URLSearchParams(searchParams);
    if (z) next.set("zone_id", z); else next.delete("zone_id");
    setSearchParams(next, { replace: true });
  };

  return (
    <div data-testid="unassigned-wards-page">
      <PageHeader title="Unassigned Wards" subtitle="Wards whose properties had no covering tax collector after auto-assign — use Assign on a row to map a collector from that zone and assign its properties in one step.">
        <div className="flex items-center gap-2 flex-wrap">
          <ZoneSelect code={code} value={uw.zoneId} onChange={onZone} disabled={uw.loading && !report} testId="unassigned-wards-zone-select" />
          <ToggleGroup type="single" variant="outline" size="sm" value={uw.source}
            onValueChange={(v) => { if (v && v !== uw.source) uw.load(v); }} aria-label="Report source" data-testid="unassigned-wards-source-toggle">
            <ToggleGroupItem value="last_run" className="text-xs px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid="unassigned-wards-source-last-run">Last run</ToggleGroupItem>
            <ToggleGroupItem value="live" className="text-xs px-3 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid="unassigned-wards-source-live">Live</ToggleGroupItem>
          </ToggleGroup>
          <Button variant="outline" size="sm" onClick={uw.source === "live" ? uw.refreshLive : uw.reload} disabled={uw.loading} data-testid="unassigned-wards-page-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${uw.loading ? "animate-spin" : ""}`} /> {uw.source === "live" ? "Refresh live" : "Refresh"}
          </Button>
          <Button size="sm" onClick={uw.exportXlsx} disabled={uw.exporting || rows.length === 0} data-testid="unassigned-wards-page-export">
            <Download className="h-3.5 w-3.5 mr-1.5" /> {uw.exporting ? "Preparing…" : "Export Excel"}
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Wards with gaps" value={s.wards ?? 0} icon={MapPinOff} sub={s.uncovered ? `${s.uncovered} still uncovered` : report ? "none uncovered" : undefined} testId="unassigned-wards-kpi-wards" />
        <KpiCard label="Unassigned properties" value={s.properties ?? 0} icon={Home} sub={report ? `of ${Number(report.scanned || 0).toLocaleString("en-IN")} scanned` : undefined} testId="unassigned-wards-kpi-properties" />
        <KpiCard label="Covered now" value={s.covered_now ?? 0} icon={CheckCircle2} sub="territory fixed — run auto-assign" testId="unassigned-wards-kpi-covered" />
        <KpiCard label="Without a ward" value={s.no_ward_properties ?? 0} icon={AlertTriangle} sub="properties missing ward data" testId="unassigned-wards-kpi-no-ward" />
      </div>

      <Card className="shadow-sm mb-4">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid="unassigned-wards-source-bar">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <SourceBadge report={report} testId="unassigned-wards-page-source-badge" />
            {report?.zone && (
              <Badge variant="outline" className="text-[11px] gap-1 bg-primary/10 text-primary border-primary/20" data-testid="unassigned-wards-zone-badge">
                Zone: {report.zone.zone_name}
                {report.all_summary ? <span className="text-muted-foreground font-normal">· {s.wards ?? 0} of {report.all_summary.wards} gap ward{report.all_summary.wards === 1 ? "" : "s"}</span> : null}
                <button type="button" onClick={() => onZone("")} className="ml-0.5 rounded hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label="Clear zone filter" data-testid="unassigned-wards-zone-clear">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )}
            {job && (
              <span className="text-xs text-muted-foreground" data-testid="unassigned-wards-job-line">
                Run {job.status === "cancelled" ? "stopped" : job.status} {fmtDateTime(job.finished_at || job.started_at)} by {job.started_by?.name || "—"} •
                {" "}{Number(job.assigned || 0).toLocaleString("en-IN")} assigned{job.moved ? `, ${Number(job.moved).toLocaleString("en-IN")} moved to the collector who now covers them` : ""},
                {" "}{Number((job.no_collector || 0) + (job.uncovered || 0)).toLocaleString("en-IN")} left without a collector
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm" data-testid="unassigned-wards-goto-users">
              <Link to={`/${code}/admin/users`}><Users className="h-3.5 w-3.5 mr-1.5" /> Fix territories</Link>
            </Button>
            <Button asChild variant="outline" size="sm" data-testid="unassigned-wards-goto-assignments">
              <Link to={`/${code}/supervisor/assignments`}><Wand2 className="h-3.5 w-3.5 mr-1.5" /> Run auto-assign</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      <ReportNotes report={report} />

      {/* Phase 166: wards of a divided zone that no supervisor sees — admin-only until mapped */}
      {(report?.no_supervisor_wards || []).length > 0 && (
        <Card className="shadow-sm mt-3 border-amber-300/70 bg-amber-50/60 dark:bg-amber-950/20" data-testid="no-supervisor-wards-card">
          <CardContent className="p-4 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium flex items-center gap-2 text-amber-900 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4" /> {report.no_supervisor_wards.length} ward{report.no_supervisor_wards.length === 1 ? "" : "s"} with no supervisor
              </p>
              <Button asChild size="sm" variant="outline" className="h-8">
                <Link to={`/${code}/admin/users?role=supervisor`} data-testid="no-supervisor-wards-fix-link"><Users className="h-3.5 w-3.5 mr-1.5" /> Map a supervisor</Link>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              These zones were divided among supervisors, but nobody holds these wards — their collectors, properties and collections are visible to the admin only.
            </p>
            <ul className="flex flex-wrap gap-1.5" data-testid="no-supervisor-wards-list">
              {report.no_supervisor_wards.map((w) => (
                <li key={w.id}>
                  <Badge variant="outline" className="bg-background text-xs font-normal" data-testid={`no-supervisor-ward-${w.name}`}>
                    {w.name} <span className="text-muted-foreground ml-1">· {w.zone_name}{w.holders?.length ? ` · zone held by ${w.holders.map((h) => h.name).join(", ")}` : ""}</span>
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Phase 156: wards whose properties have no collector who can see them today (independent of the last run) */}
      <CoverageAlertsCard code={code} compact refreshKey={alertsKey} className="mt-3" onChanged={uw.refreshLive} />

      <Card className="shadow-sm mt-3">
        <CardContent className="p-0">
          {uw.loading && !report ? (
            <TableRowsSkeleton rows={6} cols={6} testId="unassigned-wards-page-skeleton" />
          ) : uw.error ? (
            <div className="p-8 text-center space-y-2" data-testid="unassigned-wards-page-error">
              <p className="text-sm text-destructive">{errMsg(uw.error)}</p>
              <Button size="sm" variant="outline" onClick={uw.reload}>Retry</Button>
            </div>
          ) : rows.length === 0 ? (
            <UnassignedWardsEmpty report={report} />
          ) : (
            <UnassignedWardsTable rows={rows} code={code} onAssigned={uw.applyAssignResult} onBackfilled={() => { uw.refreshLive(); setAlertsKey((k) => k + 1); }} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
